import React, { useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import BASE_URL from "../config";
import {
  CaciStyles, CaciDetailStyles, PageHeader, Pager, ArticleCard,
  SkelGrid, SectionNote, Prose, SearchField, PerPageSelect, usePagedList,
  useReveal, useScrollProgress,
  stripHtml, excerptOf, truncate, langPath, localized, localizedImage,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   SERVICEDETAIL
   ───────────────────────────────────────────────────────────────────────────
   This one is really two things at once: a service description, and the
   paginated list of pages belonging to it. The endpoint reflects that —
   /api/serviceportail/edit/:id returns { service, pages: { data, pagination } }
   — so the listing half runs on the shared usePagedList hook and the service
   itself rides along in `extra`.

   Fixes:
   · The service description was rendered with no typography and no scroll
     handling for tables; it now uses the same prose rules as the article body.
   · Filters live in the URL, so a page you navigated to can be shared and
     survives a refresh.
   · Real disabled pagination buttons — the old markup put `disabled` on <a>
     tags, which does nothing, so "previous" fired a request for page 0.
   · The heading was empty until the request returned, so the tab title read
     "undefined"; a labelled fallback now holds the space.
   · The list dims instead of blanking on every keystroke, and a failed
     request is distinct from an empty one.
   ═══════════════════════════════════════════════════════════════════════════ */

function ServiceDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const inputRef = useRef(null);

  const select = useCallback((data) => ({
    items: data?.pages?.data,
    pagination: data?.pages?.pagination,
    extra: data?.service ?? null,
  }), []);

  const list = usePagedList({
    url: `${BASE_URL}/api/serviceportail/edit/${id}`,
    select,
    perPageOptions: [6, 12, 24, 48],
    defaultPerPage: 12,
  });

  const pageRef = useReveal([list.items, list.state, list.extra]);
  const progress = useScrollProgress();

  const service = list.extra;
  const title = service ? stripHtml(localized(service, "name", lang)) : "";
  const heading = title || t("menu.Services", "Service");
  const bodyHtml = service ? localized(service, "description", lang) : "";
  const summary = truncate(stripHtml(bodyHtml), 180);

  const clearSearch = () => {
    list.setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const linkFor = (p) => (p.slug ? `/page/${p.id}/${p.slug}` : `/page/${p.id}`);
  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <CaciDetailStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{heading} — {t("menu.caci")}</title>
        <meta name="description" content={summary || heading} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={heading} />
        <meta property="og:description" content={summary || heading} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:site_name" content={t("menu.caci")} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={heading}
        crumbs={[
          { label: t("menu.home"), to: langPath(lang, "/") },
          { label: t("menu.Toutpage", "Pages"), to: langPath(lang, "/toutpage") },
          { label: truncate(heading, 40) },
        ]}
        count={
          list.state === "ready" && list.total > 0
            ? { value: list.total.toLocaleString(nf), label: t("menu.Toutpage", "Pages") }
            : null
        }
      />

      {/* The service's own description, above its list of pages. */}
      {list.state === "ready" && bodyHtml && (
        <section className="caci-section svc-intro">
          <div className="caci-shell">
            <Prose html={bodyHtml} />
          </div>
        </section>
      )}

      <div className="bar">
        <div className="caci-shell">
          <div className="bar-inner">
            <SearchField
              inputRef={inputRef}
              value={list.term}
              onChange={list.setTerm}
              onClear={clearSearch}
              placeholder={t("search.placeholder")}
              busy={list.refetching}
              clearLabel={t("home.Close")}
            />
            <div className="bar-filters">
              <PerPageSelect
                value={list.perPage}
                options={list.perPageOptions}
                onChange={(v) => list.patch({ per: v })}
                label={t("search.perPage")}
              />
            </div>
          </div>
        </div>
      </div>

      <section className="caci-section" ref={list.listRef}>
        <div className="caci-shell">
          {list.state === "loading" && <SkelGrid count={Math.min(list.perPage, 12)} />}

          {list.state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Chargement impossible")}
              text={t("home.section_error", "Ce service n'a pas pu être chargé.")}
              action={
                <button type="button" className="btn-quiet" onClick={list.reload}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {list.state === "ready" && list.items.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                list.isFiltered ? (
                  <button type="button" className="btn-quiet" onClick={list.clearAll}>
                    <i className="bi bi-arrow-counterclockwise" />
                    {t("search.clear", "Tout effacer")}
                  </button>
                ) : null
              }
            />
          )}

          {list.state === "ready" && list.items.length > 0 && (
            <>
              <div className={`caci-grid${list.refetching ? " is-busy" : ""}`}>
                {list.items.map((p, i) => (
                  <ArticleCard
                    key={p.id ?? i}
                    index={i}
                    to={linkFor(p)}
                    image={localizedImage(p, "image", lang, BASE_URL)}
                    title={truncate(localized(p, "name", lang), 78)}
                    excerpt={excerptOf(localized(p, "description", lang), 20)}
                    readLabel={t("home.Read_More")}
                    isRtl={isRtl}
                  />
                ))}
              </div>

              <div className="pager-wrap">
                <Pager
                  page={list.page}
                  totalPages={list.totalPages}
                  onChange={list.goToPage}
                  labels={{
                    first: t("search.first", "Première page"),
                    prev: t("search.prev", "Page précédente"),
                    next: t("search.next", "Page suivante"),
                    last: t("search.last", "Dernière page"),
                    page: t("search.page", "Page"),
                    of: t("search.of", "sur"),
                  }}
                />
              </div>
            </>
          )}

          <p className="caci-sr" aria-live="polite">
            {list.state === "ready" ? `${list.total} ${t("search.results", "résultats")}` : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
.caci-page .svc-intro { padding-block-end: 0; }
.caci-page .caci-grid.is-busy { opacity: .45; pointer-events: none; transition: opacity .2s ease; }
.caci-page .pager-wrap { margin-block-start: clamp(36px, 6vw, 64px); }
`;

export default ServiceDetail;
